import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { OrganizationType, ORGANIZATION_PRESETS } from '@/types/organizationCustomizationTypes';
import HivePackDemo from '@/components/demo/HivePackDemo';
import { 
  Music, 
  Dumbbell, 
  Trees, 
  Waves, 
  Zap, 
  Crown, 
  GraduationCap,
  School,
  Settings
} from 'lucide-react';

interface HivePackOption {
  type: OrganizationType;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  benefits: string[];
  color: string;
}

const HIVE_PACKS: HivePackOption[] = [
  {
    type: 'dance',
    name: 'Dance Studios',
    description: 'Perfect for dance studios with recital management',
    icon: Music,
    benefits: ['Recital Management', 'Competition Tracking', 'Costume & Apparel Shop'],
    color: 'bg-pink-500'
  },
  {
    type: 'gymnastics', 
    name: 'Gymnastics',
    description: 'Designed for gymnastics centers and meets',
    icon: Dumbbell,
    benefits: ['Meet Management', 'Skill Progression', 'Equipment Tracking'],
    color: 'bg-purple-500'
  },
  {
    type: 'equestrian',
    name: 'Equestrian',
    description: 'Horse riding lessons and stable management',
    icon: Crown,
    benefits: ['Horse Management', 'Show Registration', 'Stable Operations'],
    color: 'bg-amber-600'
  },
  {
    type: 'swim',
    name: 'Swim Schools',
    description: 'Swimming lessons with stroke progression',
    icon: Waves,
    benefits: ['Stroke Tracking', 'Pool Scheduling', 'Safety Certifications'],
    color: 'bg-blue-500'
  },
  {
    type: 'martial_arts',
    name: 'Martial Arts',
    description: 'Martial arts schools with belt progression',
    icon: Zap,
    benefits: ['Belt Progression', 'Tournament Management', 'Testing Schedules'],
    color: 'bg-red-500'
  },
  {
    type: 'music',
    name: 'Music Schools',
    description: 'Music education with recital management',
    icon: Music,
    benefits: ['Instrument Tracking', 'Recital Planning', 'Practice Logs'],
    color: 'bg-green-500'
  },
  {
    type: 'camp',
    name: 'Summer Camps',
    description: 'Camp programs with activity management',
    icon: Trees,
    benefits: ['Activity Scheduling', 'Cabin Management', 'Trip Planning'],
    color: 'bg-emerald-500'
  },
  {
    type: 'afterschool',
    name: 'After School',
    description: 'After-school programs and enrichment',
    icon: School,
    benefits: ['Pickup Tracking', 'Homework Help', 'Enrichment Activities'],
    color: 'bg-indigo-500'
  },
  {
    type: 'multi',
    name: 'Multi-Activity',
    description: 'Centers offering multiple activity types',
    icon: Settings,
    benefits: ['Flexible Programs', 'Cross-Activity Enrollment', 'Unified Management'],
    color: 'bg-gray-500'
  }
];

interface HivePackSettingsProps {
  organizationId: string;
  currentOrganizationType?: OrganizationType | null;
}

const HivePackSettings: React.FC<HivePackSettingsProps> = ({ 
  organizationId, 
  currentOrganizationType 
}) => {
  const [selectedPack, setSelectedPack] = useState<OrganizationType | null>(currentOrganizationType || null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSelectPack = async (packType: OrganizationType) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ organization_type: packType })
        .eq('id', organizationId);

      if (error) throw error;

      setSelectedPack(packType);
      toast.success(`HivePack updated to ${HIVE_PACKS.find(p => p.type === packType)?.name}!`);
      
      // Reload the page to apply the new customizations
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('Error updating HivePack:', error);
      toast.error('Failed to update HivePack. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getTerminologyPreview = (packType: OrganizationType) => {
    const preset = ORGANIZATION_PRESETS[packType];
    if (!preset?.terminology) return null;
    
    return [
      `${preset.terminology.student} (students)`,
      `${preset.terminology.class} (classes)`,
      `${preset.terminology.instructor} (instructors)`
    ].filter(Boolean).slice(0, 3);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Choose Your HivePack</h2>
        <p className="text-muted-foreground mt-2">
          Select the HivePack that best matches your business to unlock specialized features, 
          terminology, and workflows designed to help you save time, grow revenue, and reduce costs.
        </p>
      </div>

      {selectedPack && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Crown className="h-5 w-5" />
              Current HivePack: {HIVE_PACKS.find(p => p.type === selectedPack)?.name}
            </CardTitle>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {HIVE_PACKS.map((pack) => {
          const IconComponent = pack.icon;
          const isSelected = selectedPack === pack.type;
          const terminologyPreview = getTerminologyPreview(pack.type);
          
          return (
            <Card 
              key={pack.type}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                isSelected ? 'ring-2 ring-primary border-primary' : ''
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-lg ${pack.color} text-white`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  {isSelected && (
                    <Badge variant="default" className="bg-green-500">
                      Active
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg">{pack.name}</CardTitle>
                <CardDescription>{pack.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm mb-2">Key Benefits:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {pack.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-current rounded-full" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {terminologyPreview && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Custom Terminology:</h4>
                    <div className="flex flex-wrap gap-1">
                      {terminologyPreview.map((term, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {term}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={() => handleSelectPack(pack.type)}
                  disabled={isUpdating || isSelected}
                  className="w-full"
                  variant={isSelected ? "outline" : "default"}
                >
                  {isUpdating ? 'Updating...' : 
                   isSelected ? 'Currently Active' : 'Select This Pack'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800">💡 Pro Tip</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-700">
            Once you select a HivePack, your entire app experience will be customized for your industry. 
            You'll see specialized terminology, relevant features, and workflow optimizations designed 
            specifically for your type of business.
          </p>
        </CardContent>
      </Card>

      {/* Live Demo Section */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4">Live Preview</h3>
        <HivePackDemo />
      </div>
    </div>
  );
};

export default HivePackSettings;